import { initTdx } from '../../../init/sdkTdx';
import { tdx_request, tdx_response } from "../../../tdx_proto/bundle";
import { getConnectionPool } from '../../../core/connectionPool';
import { hashString } from '../../../utils/hash';

async function getClient() {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (times < 0) throw new Error("Connection Failed. Please try again.");
    return { client, pin, data };
}

export async function GetFileDetails(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.FileDetails.IFileDetail | null> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        fileDetails: tdx_request.FileDetails.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.fileDetails?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get file details ${res?.fileDetails?.status}`);
        }
        return res?.fileDetails?.details || null;
    } catch (err) {
        client.close();
        throw Error('Error in GetFileDetails request: ' + err);
    }
}

export async function GetFileSummary(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.FileSummary.ISummary | null> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        fileSummary: tdx_request.FileSummary.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.fileSummary?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get file summary ${res?.fileSummary?.status}`);
        }
        return res?.fileSummary?.summary || null;
    } catch (err) {
        client.close();
        throw Error('Error in GetFileSummary request: ' + err);
    }
}

export async function GetLifecycleEvents(
    fileId: string,
    startTime: number,
    endTime: number,
    limit: number = 100,
    offset: number = 0
): Promise<tdx_response.LifecycleEvents.IEvent[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        lifecycleEvents: tdx_request.LifecycleEvents.create({ pin, data, fileId, startTime, endTime, limit, offset })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.lifecycleEvents?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get lifecycle events ${res?.lifecycleEvents?.status}`);
        }
        return res?.lifecycleEvents?.events || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetLifecycleEvents request: ' + err);
    }
}

export async function GetVersionHistory(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.VersionHistory.IVersion[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        versionHistory: tdx_request.VersionHistory.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.versionHistory?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get version history ${res?.versionHistory?.status}`);
        }
        return res?.versionHistory?.versions || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetVersionHistory request: ' + err);
    }
}

export async function GetShares(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.Shares.IShare[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        shares: tdx_request.Shares.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.shares?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get shares ${res?.shares?.status}`);
        }
        return res?.shares?.shares || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetShares request: ' + err);
    }
}

export async function GetPathHistory(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.PathHistory.IPathChange[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        pathHistory: tdx_request.PathHistory.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.pathHistory?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get path history ${res?.pathHistory?.status}`);
        }
        return res?.pathHistory?.changes || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetPathHistory request: ' + err);
    }
}

export async function GetActorStats(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.ActorStats.IActorStat[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        actorStats: tdx_request.ActorStats.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.actorStats?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get actor stats ${res?.actorStats?.status}`);
        }
        return res?.actorStats?.actors || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetActorStats request: ' + err);
    }
}

export async function GetCountryStats(
    fileId: string,
    startTime: number,
    endTime: number
): Promise<tdx_response.CountryStats.ICountryStat[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        countryStats: tdx_request.CountryStats.create({ pin, data, fileId, startTime, endTime })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.countryStats?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get country stats ${res?.countryStats?.status}`);
        }
        return res?.countryStats?.countries || [];
    } catch (err) {
        client.close();
        throw Error('Error in GetCountryStats request: ' + err);
    }
}

export async function ListFilesForDashboard(
    params: {
        startTime: number,
        endTime: number,
        query?: string,
        ownerEmail?: string,
        sharedTeam?: string,
        fileTypes?: string[],
        minSizeMb?: number,
        maxSizeMb?: number,
        modifiedFrom?: number,
        modifiedTo?: number
    }
): Promise<tdx_response.ListFilesForDashboard.IFileItem[]> {
    const { client, pin, data } = await getClient();
    const req = tdx_request.Request.create({
        listFilesForDashboard: tdx_request.ListFilesForDashboard.create({
            pin, data,
            startTime: params.startTime,
            endTime: params.endTime,
            query: params.query || "",
            ownerEmail: params.ownerEmail || "",
            sharedTeam: params.sharedTeam || "",
            fileTypes: params.fileTypes || [],
            minSizeMb: params.minSizeMb || 0,
            maxSizeMb: params.maxSizeMb || 0,
            modifiedFrom: params.modifiedFrom || 0,
            modifiedTo: params.modifiedTo || 0
        })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.listFilesForDashboard?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to list files for dashboard ${res?.listFilesForDashboard?.status}`);
        }
        return res?.listFilesForDashboard?.files || [];
    } catch (err) {
        client.close();
        throw Error('Error in ListFilesForDashboard request: ' + err);
    }
}
